import { useState, useEffect, useCallback } from 'react';
import { LoadingState } from 'shared/LoadingState';

export interface PaginatedSearchOptions {
  pageOptions?: number[];
  initialItemsPerPage?: number;
  debounceDelay?: number;
}

export interface PaginatedSearchState {
  searchTerm: string;
  debouncedSearchTerm: string;
  page: number;
  itemsPerPage: number;
  loadingState: LoadingState;
}

export interface PaginatedSearchActions {
  setSearchTerm: (term: string) => void;
  setPage: (page: number) => void;
  setItemsPerPage: (itemsPerPage: number) => void;
  setLoadingState: React.Dispatch<React.SetStateAction<LoadingState>>;
  handleSearchChange: (event: CustomEvent<{ value: string }>) => void;
  handlePaginationChange: (event: CustomEvent<number>) => void;
  handleItemsPerPageChange: (event: CustomEvent<number>) => void;
  resetToFirstPage: () => void;
}

export interface PaginatedSearchResult extends PaginatedSearchState, PaginatedSearchActions {
  pageOptions: number[];
}

export const usePaginatedSearch = (options: PaginatedSearchOptions = {}): PaginatedSearchResult => {
  const { pageOptions = [5, 10, 15], initialItemsPerPage = 5, debounceDelay = 500 } = options;

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage);
  const [loadingState, setLoadingState] = useState<LoadingState>({
    isLoading: true,
    hasError: false,
  });

  // Debounce effect for search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, debounceDelay);

    return () => clearTimeout(timer);
  }, [searchTerm, debounceDelay]);

  // Event handlers
  const handleSearchChange = useCallback((event: CustomEvent<{ value: string }>): void => {
    setSearchTerm(event.detail.value);
    setPage(1); // Reset to first page when searching
  }, []);

  const handlePaginationChange = useCallback((event: CustomEvent<number>): void => {
    setPage(event.detail);
  }, []);

  const handleItemsPerPageChange = useCallback((event: CustomEvent<number>): void => {
    setItemsPerPage(event.detail);
    setPage(1);
  }, []);

  const resetToFirstPage = useCallback((): void => {
    setPage(1);
  }, []);

  return {
    // State
    searchTerm,
    debouncedSearchTerm,
    page,
    itemsPerPage,
    loadingState,
    pageOptions,

    // Actions
    setSearchTerm,
    setPage,
    setItemsPerPage,
    setLoadingState,
    handleSearchChange,
    handlePaginationChange,
    handleItemsPerPageChange,
    resetToFirstPage,
  };
};
